import Neutralino from "@neutralinojs/lib";
import { useEffect, useState } from "react";
import { useIsDesktop } from "./useIsDesktop";

interface NeutralinoType {
  os: {
    open: (url: string) => Promise<void>;
  };
}

export const useOpenUrl = () => {
  const isDesktop = useIsDesktop();
  const [neutralinoReady, setNeutralinoReady] = useState(false);

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    // Check if Neutralino is already ready
    if (window.__NEUTRALINO_READY__) {
      setNeutralinoReady(true);
      return;
    }

    // Listen for Neutralino ready event
    const handleNeutralinoReady = () => {
      console.log("📡 useOpenUrl: Neutralino ready event received");
      setNeutralinoReady(true);
    };

    window.addEventListener("neutralino-ready", handleNeutralinoReady);

    return () => {
      window.removeEventListener("neutralino-ready", handleNeutralinoReady);
    };
  }, [isDesktop]);

  console.log("useOpenUrl hook initialized:", {
    isDesktop,
    neutralinoReady,
    hasWindowNeutralino:
      typeof window !== "undefined" ? !!window.Neutralino : "window undefined",
    hasImportedNeutralino: !!Neutralino,
    windowLocation:
      typeof window !== "undefined"
        ? {
            protocol: window.location.protocol,
            port: window.location.port,
            host: window.location.host,
          }
        : "window undefined",
  });

  return async (url: string) => {
    console.log("useOpenUrl called with:", {
      url,
      isDesktop,
      neutralinoReady,
      hasWindowNeutralino: !!window.Neutralino,
      hasImportedNeutralino: !!Neutralino,
      neutralinoObject: window.Neutralino
        ? "window.Neutralino present"
        : "window.Neutralino missing",
    });

    if (isDesktop && neutralinoReady) {
      console.log(
        "✅ Desktop mode detected and Neutralino is ready - will try Neutralino",
      );

      // Try window.Neutralino first, then fall back to imported Neutralino
      const neutralinoApi = window.Neutralino || Neutralino;

      if (neutralinoApi) {
        try {
          const neu = neutralinoApi as NeutralinoType;
          const apiSource = window.Neutralino
            ? "window.Neutralino"
            : "imported Neutralino";
          console.log(`✅ Using ${apiSource}, calling os.open with:`, url);
          await neu.os.open(url);
          console.log("✅ Neutralino.os.open completed successfully");
          return;
        } catch (error) {
          console.error("❌ Error calling Neutralino.os.open:", error);
          // Fall back to regular window.open if Neutralino fails
        }
      } else {
        console.warn(
          "❌ Neither window.Neutralino nor imported Neutralino available",
        );
      }
    } else if (isDesktop && !neutralinoReady) {
      console.log(
        "⏳ Desktop mode detected but Neutralino not ready yet - falling back to window.open",
      );
    } else {
      console.log("❌ Desktop mode NOT detected - will use window.open");
    }

    // Fallback to regular window.open
    console.log("Using window.open as fallback");
    window.open(url, "_blank");
  };
};
